import { useQuery, useSubscription } from "@apollo/client"
import { Box, Typography } from "@mui/material"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import GroupJobTile from "../../components/JobTile/GroupJobTile"
import { JobTile } from "../../components/JobTile/JobTile"
import { CenterCircular } from "../../components/Loading"
import { useJobContext } from "../../contexts/JobContext"
import ShiftContext from "../../contexts/ShiftContext"
import { GET_JOB_LISTING_QUERY } from "../../entities/job/graphql/queries"
import { getNextJobStatus, splitJobIntoPickupAndDelivery, useUpdateStatusHook } from "../../entities/job/helper"
import { IJob, IShiftJobs } from "../../entities/job/interface"
import { useUseableHeight, useWindowDimensions } from "../../utils/styling"
import { GET_VEHICLE_SUBSCRIPTION } from "../../entities/vehicle/graphql/subscription"
import { GET_JOBLIST_SUBSCRIPTION } from "../../entities/job/graphql/subscription"
import dayjs from "dayjs"

const swipeThreshold = 60 // percentage of the screen width to swipe before action is triggered

const JobListing = () => {
    const { t } = useTranslation()
    const [jobListing, setJobListing] = useState<IShiftJobs>()
    const [jobRecords, setJobRecords] = useState<IJob[]>([])
    const { handleJobUpdate, jobUpdateMutationLoading } = useUpdateStatusHook()
    const navigate = useNavigate()
    const context = useContext(ShiftContext)
    const { setIndexes, startNewJobPart, setIsConsecutive } = useJobContext()
    const height = useUseableHeight()
    const date = dayjs().format("YYYY-MM-DD")

    //swipe threshold
    const { width } = useWindowDimensions()
    const thresholdX = useMemo(() => (width * swipeThreshold) / 100, [width])

    // Handle context missing
    if (!context) {
        throw new Error("ShiftContext is not provided")
    }
    const { vehicleId, shiftId, selectedShift, setStops, jobs: shiftLoadedJobs, completedJobIdsFromStorage } = context
    const { data: vehicleSubscriptionData } = useSubscription(GET_VEHICLE_SUBSCRIPTION, {
        variables: { vehicleId: vehicleId },
    })

    // Fetch job listings based on vehicle and shift
    const { data: jobListingData, loading } = useQuery(GET_JOB_LISTING_QUERY, {
        variables: {
            filter: {
                assignedShift: {
                    vehicle: vehicleId,
                    shiftId: shiftId,
                },
                _operators: { jobStatusHistory: { status: { nin: ["completed", "canceled", "onHold"] } } },
            },
        },
        skip: !vehicleId || !shiftId,
        fetchPolicy: "cache-and-network", //why? //added this because while navigation apollo does not fetch data again (only on page refresh)
    })

    const { data: completedJobsFromQuery } = useQuery(GET_JOB_LISTING_QUERY, {
        variables: {
            filter: {
                _operators: { _id: { in: completedJobIdsFromStorage } }, // Filter for IDs not already in completedJobs
            },
        },
        skip: completedJobIdsFromStorage.length === 0,
        fetchPolicy: "cache-and-network",
    })

    const { data: subscriptionData } = useSubscription(GET_JOBLIST_SUBSCRIPTION, {
        variables: { dateFrom: date, dateTo: date },
    })

    useEffect(() => {
        if (subscriptionData) {
            setJobRecords((prevJobs: IJob[]) => {
                const jobMap = new Map(prevJobs?.map((job) => [job._id, job]))
                const currentJob = subscriptionData?.jobInDateRange
                const removeJob =
                    currentJob._metadata?.deleted ||
                    currentJob.jobStatus === "canceled" ||
                    currentJob.jobStatus === "onHold"
                const unAssignedJob = currentJob.assignedShift?.vehicle !== vehicleId
                const unShiftJob = currentJob.assignedShift?.shiftId !== shiftId
                if (removeJob || unAssignedJob || unShiftJob) {
                    jobMap.delete(currentJob._id)
                } else {
                    jobMap.set(currentJob._id, currentJob)
                }
                return Array.from(jobMap.values())
            })
        }
    }, [vehicleSubscriptionData])

    useEffect(() => {
        if (!jobListingData?.jobsList?.records) return

        const jobsFromQuery = window.navigator.onLine
            ? jobListingData.jobsList.records
            : Object.values(shiftLoadedJobs || {})

        const completedJobs =
            completedJobsFromQuery?.jobsList?.records?.map((job: IJob) => ({
                ...job,
                isJobCompleted: true,
            })) || []

        setJobRecords([...completedJobs, ...jobsFromQuery])
    }, [jobListingData?.jobsList?.records, completedJobsFromQuery?.jobsList?.records])

    useEffect(() => {
        const shift = vehicleSubscriptionData?.vehicle?.shifts[0] || selectedShift
        const processedJobs = splitJobIntoPickupAndDelivery(shift, jobRecords)
        setStops(processedJobs.ids)
        setJobListing(processedJobs.jobs)
    }, [selectedShift, jobRecords, setStops, shiftLoadedJobs])

    // Handle the job which is being started or swiped
    const handleJobStarted = async (job: IJob) => {
        //filter job from job listing with same group id to that job._id
        const jobGroup = jobListing && jobListing[job.groupId || job._id]

        //validations need to be added
        //check if job is not already in progress
        const jobId = job._id
        const jobStatus = job.jobStatus
        const isJobCompleted = job?.isJobCompleted
        let isJobUpdated = false
        //get next job status
        const nextJobStatus = getNextJobStatus(jobStatus)
        if (!nextJobStatus || !isJobCompleted) {
            console.log("Invalid job status")
        }

        // check if job is group job and has multiple stops
        if (jobGroup && jobGroup.destinationStops[0].length > 1) {
            setIsConsecutive(true)
            window.sessionStorage.setItem("screens", JSON.stringify({ consecutive: "true", roadMap: "false" }))
            setIndexes({ groupId: job.groupId || job._id, destinationIndex: 0, stopIndex: 0 })
            navigate("/job")
        } else {
            // Update job status to "In Progress"
            try {
                if (!isJobCompleted) {
                    isJobUpdated = await handleJobUpdate({ jobStatus: "onRouteToPickup" }, jobId)
                }
                // Handle success: job is started navigate to started page
                if (isJobUpdated || isJobCompleted) {
                    // Set the selected job in the context
                    setIndexes({ groupId: job.groupId || job._id, destinationIndex: 0, stopIndex: 0 })
                    startNewJobPart(job._id, "pickup")
                    // Navigate to the job started page
                    navigate("/job")
                    return
                }
            } catch (error) {
                // Handle error: open modal with error message
                console.error(error)
            }
        }
    }

    // end of touch events

    if (loading || jobUpdateMutationLoading) {
        return <CenterCircular />
    }
    return (
        <>
            <Box
                width={"100%"}
                bgcolor={"secondary.main"}
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                height={height}
                padding={2}
                paddingTop={0}
                overflow={"auto"}
                sx={{ overflowX: "hidden" }}
            >
                {!jobListing || Object.keys(jobListing)?.length === 0 ? (
                    <Typography variant="h6" color="text.secondary" padding={2}>
                        {t("jobs.mobile.details.noJobsAvailable")}
                    </Typography>
                ) : (
                    <>
                        <Typography variant="body1" color="text.secondary" mt={1}>
                            {t("jobs.mobile.details.swipeJobToStart")}
                        </Typography>
                        {jobListing &&
                            Object.entries(jobListing).map(([groupId, jobs]) => {
                                if (!jobs || jobs.destinationStops.length === 0) {
                                    return null // Return null if no jobs exist for this groupId
                                }

                                const commonProps = {
                                    index: groupId,
                                    handleJobStarted,
                                    swipeThreshold: thresholdX,
                                }

                                // GroupJob Tile
                                if (jobs.groupJob) {
                                    return <GroupJobTile key={groupId} jobs={jobs.destinationStops} {...commonProps} />
                                }
                                // Normal Job Tile
                                const singleJob = jobs.destinationStops[0][0]
                                return <JobTile key={groupId} job={singleJob} isSwipe {...commonProps} />
                            })}
                    </>
                )}
            </Box>
        </>
    )
}

export default JobListing
